import type { Notification } from '@packages/domain/models/notification';
import {
  INTERVAL_FIELD,
  IS_RECURRING_FIELD,
  PAYMENT_INFO_STEP_COMPLETED,
} from 'constants/fields';
import { RESET_SELECTED_OFFER } from 'constants/offers';
import {
  ADD_DYNAMIC_EDIT_FIELD,
  CLEAN_UI_FIELD_VALUES,
  CLEAR_FIELDS,
  DISMISS_CREATE_PAYMENT_ALERT,
  FETCH_EMBEDDED,
  GO_TO_NEXT_STEP,
  GO_TO_PREV_STEP,
  GO_TO_STEP,
  HIDE_CANCEL_PAYMENT_OPTION,
  HIDE_USER_MANAGEMENT,
  RESET_FIELD_VALUE,
  RESET_FIELDS_VALUES,
  RESET_INVALID_SMS_ATTEMPT,
  RESTART,
  SAVED_SUCCESSFULLY,
  SET_AUTOFILL_SIGNUP_COMPLETED,
  SET_CARD_REVAMP_ENABLED,
  SET_CHAT_VISIBLE,
  SET_DOC_COLLECTOR_FINISHED,
  SET_EMBEDDED_CONFIRMATION_STEP,
  SET_EMBEDDED_EXPERIENCE,
  SET_FIELD_VALUE,
  SET_FIELDS_VALUES,
  SET_GATEWAY_ORIGIN,
  SET_GATEWAY_SOURCE,
  SET_JUST_COMPLETED_PAYMENT,
  SET_LRS_SIGN_SUCCESS_VISIBLE,
  SET_MAX_AMOUNT,
  SET_PAYMENT_PROCESS,
  SET_PREFILLED_FIELDS,
  SET_PREFILLED_REJECT_OR_CONFIRM,
  SET_READONLY_FIELDS,
  SET_RECEIVE_SMS_NOTIFICATION,
  SET_RECEIVE_WHATSAPP_NOTIFICATION,
  SET_REDIRECT_TO_REFERRER,
  SET_REPEAT_PAYMENT_EXPERIENCE,
  SET_REPEAT_UPOP_PAYMENT_ENABLED,
  SET_SHOULD_AUTO_LOAD_ONLINE_SCRIPT,
  SET_THEME,
  SET_TRACKING_PAGE,
  SET_UPOP_IMPROVEMENTS_ENABLED,
  SHOW_CANCEL_PAYMENT_OPTION,
  SHOW_OFFLINE_INSTRUCTIONS,
  UI_NOTIFICATION_NEW,
  UI_NOTIFICATION_REMOVE,
  UNSET_EMBEDDED_EXPERIENCE,
  UNSET_JUST_COMPLETED_PAYMENT,
  UNSET_PAYMENT_PROCESS,
  UNSET_TRACKING_PAGE,
} from 'constants/ui';
import { createEntity } from 'factories/createEntity';
import type { CreatePaymentAlertKey } from 'models/error';
import type { FieldValue } from 'models/field';
import type { RootState } from 'reducers/types';
import { getFieldByEntity } from 'selectors/entities/fields/fields';
import {
  getPayment,
  getPaymentOffer,
  isOnline,
} from 'selectors/entities/payment/payment';
import { getUser } from 'selectors/entities/user/user';
import { getFieldValue, isRepeatingPayment } from 'selectors/ui/ui';
import type { PayexThunkAction } from 'store/configureStore';

export const goToNextStep = () => ({
  type: GO_TO_NEXT_STEP,
});

export const goToPrevStep = () => ({
  type: GO_TO_PREV_STEP,
});

export const goToStep = (step: number) => ({
  type: GO_TO_STEP,
  payload: { step },
});

export const setFieldsValues = (fields: Record<string, FieldValue>) => ({
  type: SET_FIELDS_VALUES,
  payload: { fields },
});

export const savedSuccessfully = () => ({
  type: SAVED_SUCCESSFULLY,
});

export const setFieldValue =
  (name: string, value: FieldValue): PayexThunkAction =>
  (dispatch, getState) => {
    const changeInPaymentInformation =
      name === 'sender_country' || name === 'amount';

    const state = getState();

    if (changeInPaymentInformation && !isRepeatingPayment(state)) {
      dispatch({
        type: RESET_SELECTED_OFFER,
      });
    }

    return dispatch({
      type: SET_FIELD_VALUE,
      payload: { name, value },
    });
  };

export const resetFieldValue =
  (name: string): PayexThunkAction =>
  dispatch => {
    dispatch({
      type: RESET_FIELD_VALUE,
      payload: { name },
    });
  };

export const resetSelectedOffer = (): PayexThunkAction => dispatch => {
  dispatch({
    type: RESET_SELECTED_OFFER,
  });
};

export const resetFieldsValues =
  (): PayexThunkAction => (dispatch, getState) => {
    const state = getState();
    const payload = getPayment(state);

    return dispatch({
      type: RESET_FIELDS_VALUES,
      payload,
    });
  };

export const cleanUIFieldValues = () => ({
  type: CLEAN_UI_FIELD_VALUES,
});

export const setRepeatPaymentExperience =
  (isEnabled: boolean): PayexThunkAction =>
  dispatch => {
    return dispatch({
      type: SET_REPEAT_PAYMENT_EXPERIENCE,
      payload: isEnabled,
    });
  };

export const setRecipient = (recipient: string) =>
  setFieldValue('recipient', recipient);

export const setReadOnlyFields = (fields: string[]) => ({
  type: SET_READONLY_FIELDS,
  payload: fields,
});

export const setPrefilledFields = (fields: string[]) => ({
  type: SET_PREFILLED_FIELDS,
  payload: fields,
});

export const addDynamicEditField = (fieldId: string) => ({
  type: ADD_DYNAMIC_EDIT_FIELD,
  payload: fieldId,
});

export const getOfferFieldIdsToClear = (state: RootState) => {
  const { ids = [] } = getFieldByEntity(state, 'offers');

  return [...ids, INTERVAL_FIELD, IS_RECURRING_FIELD];
};
export const clearOfferFields =
  (): PayexThunkAction => (dispatch, getState) => {
    const state = getState();
    const ids = getOfferFieldIdsToClear(state);

    dispatch({
      type: CLEAR_FIELDS,
      payload: ids,
    });
  };

export const restart = () => ({
  type: RESTART,
});

export const newNotification = ({
  message,
  opts,
  type = 'success',
}: Pick<Notification, 'message' | 'opts'> & {
  type?: 'error' | 'info' | 'success';
}) => ({
  type: UI_NOTIFICATION_NEW,
  payload: createEntity({ message, opts, type }),
});

export const removeNotification = (id: string) => ({
  type: UI_NOTIFICATION_REMOVE,
  payload: id,
});

export const setEmbedded = () => ({
  type: SET_EMBEDDED_EXPERIENCE,
});

export const unsetEmbedded = () => ({
  type: UNSET_EMBEDDED_EXPERIENCE,
});

export const setGatewayOrigin = () => ({
  type: SET_GATEWAY_ORIGIN,
});

export const setGatewaySource = (source: string) => ({
  type: SET_GATEWAY_SOURCE,
  payload: source,
});

export const showOfflineInstructions = () => ({
  type: SHOW_OFFLINE_INSTRUCTIONS,
});

export const setDocCollectorFinished = (finished: boolean) => ({
  type: SET_DOC_COLLECTOR_FINISHED,
  payload: { finished },
});

export const setLrsSignSuccessVisible = (visible: boolean) => ({
  type: SET_LRS_SIGN_SUCCESS_VISIBLE,
  payload: { visible },
});

export const setPrefilledRejectOrConfirm = (status: boolean) => ({
  type: SET_PREFILLED_REJECT_OR_CONFIRM,
  payload: { status },
});

export const hideUserManagement = () => ({
  type: HIDE_USER_MANAGEMENT,
});

export const setReceiveSMSNotification = (receiveSMSNotification: boolean) => ({
  type: SET_RECEIVE_SMS_NOTIFICATION,
  payload: { receiveSMSNotification },
});

export const setReceiveWhatsappNotification = (
  receiveWhatsappNotification: boolean | null,
) => ({
  type: SET_RECEIVE_WHATSAPP_NOTIFICATION,
  payload: { receiveWhatsappNotification },
});

export const showCancelPaymentOption = () => ({
  type: SHOW_CANCEL_PAYMENT_OPTION,
});

export const hideCancelPaymentOption = () => ({
  type: HIDE_CANCEL_PAYMENT_OPTION,
});

export const fetchEmbedded = (fetching: boolean) => ({
  type: FETCH_EMBEDDED,
  payload: fetching,
});

export const setShouldAutoLoadOnlineScript =
  (): PayexThunkAction => (dispatch, getState) => {
    const state = getState();
    const paymentOffer = getPaymentOffer(state);

    if (!paymentOffer || !isOnline(state)) {
      return dispatch({
        type: SET_SHOULD_AUTO_LOAD_ONLINE_SCRIPT,
        payload: false,
      });
    }

    const isWorldpayOffer = /^worldpay/i.test(paymentOffer.paymentOption);
    const isWorldpayUnionpayOffer = /^worldpay.*unionpay/i.test(
      paymentOffer.paymentOption,
    );

    const isWorldpayWechatOffer = /^worldpay.*wechat/i.test(
      paymentOffer.paymentOption,
    );

    const shouldAutoLoadOnlineScript =
      !isWorldpayOffer || isWorldpayUnionpayOffer || isWorldpayWechatOffer;

    return dispatch({
      type: SET_SHOULD_AUTO_LOAD_ONLINE_SCRIPT,
      payload: shouldAutoLoadOnlineScript,
    });
  };

export const setRedirectToReferrer = (redirectToReferrer: boolean) => ({
  type: SET_REDIRECT_TO_REFERRER,
  payload: { redirectToReferrer },
});

export const setPaymentProcess = () => ({
  type: SET_PAYMENT_PROCESS,
});

export const unsetPaymentProcess = () => ({
  type: UNSET_PAYMENT_PROCESS,
});

export const setTheme = (theme: Record<string, string>) => ({
  type: SET_THEME,
  payload: theme,
});

export const setTrackingPage = () => ({
  type: SET_TRACKING_PAGE,
});

export const unsetTrackingPage = () => ({
  type: UNSET_TRACKING_PAGE,
});

export const setChatVisible = () => ({
  type: SET_CHAT_VISIBLE,
});

export const resetInvalidSMSAttempt = () => ({
  type: RESET_INVALID_SMS_ATTEMPT,
});

export const setMaxAmount = (amount: number) => ({
  type: SET_MAX_AMOUNT,
  payload: amount,
});

export const setEmbeddedConfirmationStep = (showConfirmationStep: boolean) => ({
  type: SET_EMBEDDED_CONFIRMATION_STEP,
  payload: showConfirmationStep,
});

export const setPayerInfoByUser =
  (): PayexThunkAction => (dispatch, getState) => {
    const SENDER_EMAIL_FIELD = 'sender_email';
    const SENDER_FIRST_NAME_FIELD = 'sender_first_name';
    const SENDER_MIDDLE_NAME_FIELD = 'sender_middle_name';

    const state = getState();

    const { email, firstName, lastName } = getUser(state);
    const senderEmail = getFieldValue(state, SENDER_EMAIL_FIELD);
    if (!senderEmail) {
      return dispatch({
        type: SET_FIELDS_VALUES,
        payload: {
          fields: {
            [SENDER_EMAIL_FIELD]: email || '',
            [SENDER_FIRST_NAME_FIELD]: firstName || '',
            [SENDER_MIDDLE_NAME_FIELD]: lastName || '',
          },
        },
      });
    }
  };

export const goBackToFirstStep = (): PayexThunkAction => dispatch => {
  dispatch(goToStep(0));
  return dispatch(setFieldValue(PAYMENT_INFO_STEP_COMPLETED, false));
};

export const setAutofillSignupCompleted = () => ({
  type: SET_AUTOFILL_SIGNUP_COMPLETED,
});

export const dismissCreatePaymentAlert = (key: CreatePaymentAlertKey) => ({
  type: DISMISS_CREATE_PAYMENT_ALERT,
  payload: key,
});

export const setCardRevampEnabled = (enabled: boolean) => ({
  type: SET_CARD_REVAMP_ENABLED,
  payload: enabled,
});

export const setJustCompletedPayment = () => ({
  type: SET_JUST_COMPLETED_PAYMENT,
});

export const unsetJustCompletedPayment = () => ({
  type: UNSET_JUST_COMPLETED_PAYMENT,
});

export const setRepeatUpopPaymentEnabled = (enabled: boolean) => ({
  type: SET_REPEAT_UPOP_PAYMENT_ENABLED,
  payload: enabled,
});

export const setUpopImprovementsEnabled = (enabled: boolean) => ({
  type: SET_UPOP_IMPROVEMENTS_ENABLED,
  payload: enabled,
});
